import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck } from 'lucide-react';
import apiClient from '../../utils/api';

export const DispatchAction: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [shipment, setShipment] = useState<any>(null);
  const [transporters, setTransporters] = useState<any[]>([]);
  const [form, setForm] = useState({ transporterId: '', vehicleId: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      apiClient.getShipment(id!),
      apiClient.listTransporters(),
    ]).then(([s, t]) => {
      setShipment(s.data.data);
      const td = t.data.data;
      setTransporters(td?.users ?? td?.rows ?? (Array.isArray(td) ? td : []));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await apiClient.dispatchShipment(id!, form);
      navigate(-1);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to dispatch shipment');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;
  if (!shipment) return <div className="empty-state"><h3>Shipment not found</h3></div>;

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
          <h1 className="page-title">Dispatch Shipment</h1>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Shipment Info</h2>
          {[
            ['Tracking #', shipment.trackingNumber],
            ['Sender', shipment.senderName],
            ['Receiver', shipment.receiverName],
            ['Destination', shipment.receiverCity],
            ['Amount', `${Number(shipment.totalAmount).toLocaleString()} RWF`],
            ['Status', shipment.status],
          ].map(([k, v]) => (
            <div key={k as string} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <span style={{ color: '#64748b', fontSize: 13 }}>{k}</span>
              <span style={{ fontWeight: 500, fontSize: 13 }}>{v}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Assign Transporter</h2>
          {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{error}</div>}
          <form onSubmit={submit}>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Transporter *</label>
              <select required value={form.transporterId} onChange={e => setForm(f => ({ ...f, transporterId: e.target.value }))}>
                <option value="">— Select Transporter —</option>
                {transporters.map(t => <option key={t.id} value={t.id}>{t.firstName} {t.lastName} {t.phone ? `(${t.phone})` : ''}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Vehicle ID / Plate Number</label>
              <input placeholder="e.g. RAB 123A" value={form.vehicleId} onChange={e => setForm(f => ({ ...f, vehicleId: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label>Notes</label>
              <textarea rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: '100%' }}>
              <Truck size={15} /> {saving ? 'Dispatching...' : 'Dispatch Shipment'}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};
